import { useEffect } from 'react';
import { Alert, Checkbox as AntCheckbox, Form, Input, InputNumber, Select } from 'antd';
import clsx from 'clsx';
import { Dayjs } from 'dayjs';
import { useTranslation } from 'react-i18next';
import { Checkbox } from '@/components/checkbox/checkbox';
import { FormModal } from '@/components/form-modal/form-modal';
import { TimePicker } from '@/components/time-picker/time-picker';
import {
  useCreateOutboundCampaign,
  useUpdateOutboundCampaign,
} from '@/hooks/use-outbound-campaigns/use-outbound-campaigns';
import {
  OutboundCampaign,
  OutboundCampaignPayload,
} from '@/services/outbound-calls-service/campaigns.service.types';
import {
  CAMPAIGN_DEFAULTS,
  CAMPAIGN_LIMITS,
  RETRY_OUTCOMES,
} from '@/services/outbound-calls-service/outbound-calls.constants';
import { OutboundScenario } from '@/services/outbound-calls-service/scenarios.service.types';
import { languageName } from '@/utils/language-name';
import { openNotification } from '@/utils/notification/notification';
import {
  dispositionLabel,
  formFieldErrors,
  fromTimeValue,
  toTimeValue,
} from '../outbound-calls.utils';
import { WeekdayChips } from '../weekday-chips';
import styles from '../outbound-calls.module.scss';

type CampaignFormValues = {
  name: string;
  scenario_id?: number;
  language?: string;
  opening_line: string;
  window_start: Dayjs | null;
  window_end: Dayjs | null;
  call_days: number[];
  max_attempts: number;
  retry_delay_minutes: number;
  retry_on: string[];
  max_callbacks: number;
  pause_between_calls_seconds: number;
};

const FIELDS = [
  'name',
  'scenario_id',
  'language',
  'opening_line',
  'window_start',
  'window_end',
  'call_days',
  'max_attempts',
  'retry_delay_minutes',
  'retry_on',
  'max_callbacks',
  'pause_between_calls_seconds',
] as const satisfies readonly (keyof CampaignFormValues)[];

const DEFAULT_VALUES: Partial<CampaignFormValues> = {
  ...CAMPAIGN_DEFAULTS,
  opening_line: '',
  window_start: toTimeValue(CAMPAIGN_DEFAULTS.window_start),
  window_end: toTimeValue(CAMPAIGN_DEFAULTS.window_end),
};

const toFormValues = (campaign: OutboundCampaign): CampaignFormValues => ({
  name: campaign.name,
  scenario_id: campaign.scenario_id,
  language: campaign.language,
  opening_line: campaign.opening_line,
  window_start: toTimeValue(campaign.window_start),
  window_end: toTimeValue(campaign.window_end),
  call_days: campaign.call_days,
  max_attempts: campaign.max_attempts,
  retry_delay_minutes: campaign.retry_delay_minutes,
  retry_on: campaign.retry_on,
  max_callbacks: campaign.max_callbacks,
  pause_between_calls_seconds: campaign.pause_between_calls_seconds,
});

const toPayload = (values: CampaignFormValues): OutboundCampaignPayload => ({
  ...values,
  name: values.name.trim(),
  opening_line: values.opening_line?.trim() ?? '',
  window_start: fromTimeValue(values.window_start) ?? undefined,
  window_end: fromTimeValue(values.window_end) ?? undefined,
});

interface CampaignFormModalProps {
  open: boolean;
  companyId: number;
  scenarios: OutboundScenario[];
  /** Edits this campaign; creates a new one when absent. */
  campaign?: OutboundCampaign;
  onClose: () => void;
  onSaved: (campaign: OutboundCampaign) => void;
}

export function CampaignFormModal({
  open,
  companyId,
  scenarios,
  campaign,
  onClose,
  onSaved,
}: CampaignFormModalProps) {
  const { t } = useTranslation(['outbound', 'common']);
  const [form] = Form.useForm<CampaignFormValues>();
  const { createCampaign, isPending: isCreating } = useCreateOutboundCampaign(companyId);
  const { updateCampaign, isPending: isUpdating } = useUpdateOutboundCampaign();

  useEffect(() => {
    if (!open) return;
    form.resetFields();
    if (campaign) form.setFieldsValue(toFormValues(campaign));
  }, [open, campaign, form]);

  // An inactive scenario cannot start a campaign, but the one a campaign already uses stays.
  const scenarioOptions = scenarios
    .filter((scenario) => scenario.active || scenario.id === campaign?.scenario_id)
    .map((scenario) => ({ value: scenario.id, label: scenario.name }));

  // Every scenario of a company carries the company's voices.
  const languages = [
    ...new Set([...(scenarios[0]?.languages ?? []), ...(campaign ? [campaign.language] : [])]),
  ];

  // A company with one voice has nothing to choose; the scenarios may arrive after opening.
  const onlyLanguage = languages.length === 1 ? languages[0] : undefined;
  useEffect(() => {
    if (open && !campaign && onlyLanguage && !form.getFieldValue('language')) {
      form.setFieldValue('language', onlyLanguage);
    }
  }, [open, campaign, onlyLanguage, form]);

  // Set in the Django admin, a disposition outside the usual four must survive a save here.
  const retryOptions = [...new Set([...RETRY_OUTCOMES, ...(campaign?.retry_on ?? [])])];

  const handleSubmit = async (values: CampaignFormValues) => {
    const payload = toPayload(values);
    try {
      const saved = campaign
        ? await updateCampaign({ id: campaign.id, payload })
        : await createCampaign(payload);
      onSaved(saved);
    } catch (error) {
      const fields = formFieldErrors(error, FIELDS, {
        taken: { name: t('outbound:campaignForm.validation.nameTaken') },
      });
      if (fields.length) {
        form.setFields(fields);
        return;
      }
      openNotification({ type: 'error', message: t('outbound:campaignForm.saveFailed') });
    }
  };

  const windowRequired = [
    { required: true, message: t('outbound:campaignForm.validation.windowRequired') },
  ];

  return (
    <FormModal
      open={open}
      title={
        campaign ? t('outbound:campaignForm.editTitle') : t('outbound:campaignForm.createTitle')
      }
      subtitle={
        campaign
          ? t('outbound:campaignForm.editSubtitle')
          : t('outbound:campaignForm.createSubtitle')
      }
      submitLabel={campaign ? t('common:actions.save') : t('common:actions.create')}
      onSubmit={() => form.submit()}
      onCancel={onClose}
      isLoading={isCreating || isUpdating}
      width={640}
    >
      {!scenarioOptions.length && (
        <Alert type="warning" showIcon message={t('outbound:campaignForm.noScenarios')} />
      )}
      <Form<CampaignFormValues>
        form={form}
        name="campaign"
        layout="vertical"
        onFinish={handleSubmit}
        initialValues={DEFAULT_VALUES}
      >
        <Form.Item
          label={t('outbound:campaignForm.fields.name')}
          name="name"
          rules={[
            {
              required: true,
              whitespace: true,
              message: t('outbound:campaignForm.validation.nameRequired'),
            },
          ]}
        >
          <Input maxLength={255} />
        </Form.Item>

        <div className={styles.fieldRow}>
          <Form.Item
            className={styles.grow}
            label={t('outbound:campaignForm.fields.scenario')}
            name="scenario_id"
            rules={[
              { required: true, message: t('outbound:campaignForm.validation.scenarioRequired') },
            ]}
          >
            <Select options={scenarioOptions} />
          </Form.Item>

          <Form.Item
            className={styles.grow}
            label={t('outbound:campaignForm.fields.language')}
            name="language"
            rules={[
              { required: true, message: t('outbound:campaignForm.validation.languageRequired') },
            ]}
          >
            <Select
              options={languages.map((code) => ({ value: code, label: languageName(code) }))}
            />
          </Form.Item>
        </div>

        <Form.Item
          label={t('outbound:campaignForm.fields.openingLine')}
          name="opening_line"
          extra={t('outbound:campaignForm.help.openingLine')}
        >
          <Input.TextArea autoSize={{ minRows: 2, maxRows: 5 }} />
        </Form.Item>

        <Form.Item
          label={t('outbound:campaignForm.fields.window')}
          extra={t('outbound:campaignForm.help.window')}
        >
          <div className={clsx(styles.fieldRow, styles.subfields)}>
            <Form.Item className={styles.grow} name="window_start" rules={windowRequired}>
              <TimePicker
                allowClear={false}
                aria-label={t('outbound:campaignForm.fields.windowStart')}
              />
            </Form.Item>
            <Form.Item
              className={styles.grow}
              name="window_end"
              dependencies={['window_start']}
              rules={[
                ...windowRequired,
                ({ getFieldValue }) => ({
                  validator: (_, value: Dayjs | null) => {
                    const start: Dayjs | null = getFieldValue('window_start');
                    return !value || !start || value.isAfter(start)
                      ? Promise.resolve()
                      : Promise.reject(new Error(t('outbound:fieldError.windowOrder')));
                  },
                }),
              ]}
            >
              <TimePicker
                allowClear={false}
                aria-label={t('outbound:campaignForm.fields.windowEnd')}
              />
            </Form.Item>
          </div>
        </Form.Item>

        <Form.Item
          label={t('outbound:campaignForm.fields.callDays')}
          name="call_days"
          rules={[
            { required: true, type: 'array', min: 1, message: t('outbound:fieldError.pickDay') },
          ]}
        >
          <WeekdayChips label={t('outbound:campaignForm.fields.callDays')} />
        </Form.Item>

        <div className={styles.fieldRow}>
          <Form.Item
            className={styles.grow}
            label={t('outbound:campaignForm.fields.maxAttempts')}
            name="max_attempts"
            rules={[{ required: true, message: t('outbound:fieldError.required') }]}
          >
            <InputNumber
              min={CAMPAIGN_LIMITS.maxAttempts.min}
              max={CAMPAIGN_LIMITS.maxAttempts.max}
              precision={0}
            />
          </Form.Item>
          <Form.Item
            className={styles.grow}
            label={t('outbound:campaignForm.fields.retryDelay')}
            name="retry_delay_minutes"
            rules={[{ required: true, message: t('outbound:fieldError.required') }]}
          >
            <InputNumber
              min={CAMPAIGN_LIMITS.retryDelayMinutes.min}
              max={CAMPAIGN_LIMITS.retryDelayMinutes.max}
              precision={0}
            />
          </Form.Item>
        </div>

        <Form.Item label={t('outbound:campaignForm.fields.retryOn')} name="retry_on">
          <AntCheckbox.Group className={styles.chips}>
            {retryOptions.map((outcome) => (
              <Checkbox key={outcome} value={outcome}>
                {dispositionLabel(outcome)}
              </Checkbox>
            ))}
          </AntCheckbox.Group>
        </Form.Item>

        <div className={styles.fieldRow}>
          <Form.Item
            className={styles.grow}
            label={t('outbound:campaignForm.fields.pause')}
            name="pause_between_calls_seconds"
            rules={[{ required: true, message: t('outbound:fieldError.required') }]}
          >
            <InputNumber
              min={CAMPAIGN_LIMITS.pauseSeconds.min}
              max={CAMPAIGN_LIMITS.pauseSeconds.max}
              precision={0}
            />
          </Form.Item>
          <Form.Item
            className={styles.grow}
            label={t('outbound:campaignForm.fields.maxCallbacks')}
            name="max_callbacks"
            extra={t('outbound:campaignForm.help.maxCallbacks')}
            rules={[{ required: true, message: t('outbound:fieldError.required') }]}
          >
            <InputNumber
              min={CAMPAIGN_LIMITS.maxCallbacks.min}
              max={CAMPAIGN_LIMITS.maxCallbacks.max}
              precision={0}
            />
          </Form.Item>
        </div>
      </Form>
    </FormModal>
  );
}
